// API URL helper: handles dev (root /) vs prod (/m0/ subpath) deploy correctly.
//
// CRITICAL: In production deploy under /m0/ subpath, the MSW service worker is
// registered at /m0/mockServiceWorker.js with scope /m0/. Service workers can
// only intercept requests WITHIN their scope. If we use '/api/v1/...' (root),
// requests fall outside the SW scope → nginx returns HTML fallback → JSON
// parse errors in the FE.
//
// Solution: prefix all API URLs with BASE_URL so requests go to /m0/api/v1/...
// in production (within SW scope) and /api/v1/... in dev (root, SW at root).
//
// TEST ENV: MSW v2 in node env (vitest) does NOT auto-resolve relative URLs
// to localhost the way browser MSW does. Prepend an absolute origin so the
// same handler patterns + fetch calls match. Production unchanged.

// Vitest sets import.meta.env.MODE to 'test' for both happy-dom and node
// environments. Combined with window check to detect node-env specifically.
import { useAuthStore } from '../store/auth'
import { matchReportRequest } from './problem-reporting/registry'
import { problemReportEpoch, recordProblemReportFailure } from './problem-reporting/runtime'
import type { ReportCaptureEpoch } from './problem-reporting/runtime'

const isVitestNode =
  import.meta.env.MODE === 'test' && typeof window === 'undefined'

const TEST_ORIGIN = 'http://localhost'

// "Real engine" toggle: when VITE_API_BASE is set (e.g. http://localhost:8000)
// the app talks to the real backend instead of the MSW-intercepted relative
// path. Unset (default) = maket/simulation mode — existing behavior unchanged so
// nothing breaks (Volkan ratify: parallel transition, maket stays default).
const REAL_API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(
  /\/$/,
  '',
)

export const USE_REAL_API = Boolean(REAL_API_BASE)

const API_BASE_PATH = REAL_API_BASE
  ? `${REAL_API_BASE}/api/v1`
  : isVitestNode
    ? `${TEST_ORIGIN}${import.meta.env.BASE_URL}api/v1`
    : `${import.meta.env.BASE_URL}api/v1`

export const API_BASE = API_BASE_PATH

export function apiUrl(path: string): string {
  return `${API_BASE_PATH}${path}`
}

// Cutover: many screens call fetch(apiUrl(...)) directly (not the orval client),
// so a single global wrapper is the one reliable place to attach the real-auth
// bearer token AND transparently refresh it on expiry. Installed once at startup
// in both modes. MSW mode retains its original request path and adds only the
// default-off final-outcome collector. Only exact API-origin/base URLs are touched.
let authFetchInstalled = false

// Platform-Admin "view-as" org context (D-5 Ring 3). When the admin enters an
// org via the Org-access switcher, the org-context store sets this; the fetch
// wrapper then sends it as X-Act-As-Org so the backend scopes the request to
// that org (audited). Cleared on exit. Module-level so the non-React fetch
// wrapper can read it.
let actAsOrgId: string | null = null

export function setActAsOrg(orgId: string | null): void {
  actAsOrgId = orgId
}

// Single in-flight refresh shared by all concurrent 401s — when the 60-min
// access token expires, every pending request 401s at once; we must rotate the
// refresh token exactly ONCE, not once per request.
let refreshInFlight: Promise<boolean> | null = null

function attachBearer(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  original: typeof fetch,
  token: string | null,
  // Post-refresh retry MUST replace a caller-supplied Authorization header:
  // orval-client pre-attaches the token, so on retry the header carries the
  // now-stale bearer and the `has()` guard would keep it stuck on 401.
  overwriteAuth = false,
): Promise<Response> {
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  )
  if (token && (overwriteAuth || !headers.has('Authorization'))) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (actAsOrgId && !headers.has('X-Act-As-Org')) {
    headers.set('X-Act-As-Org', actAsOrgId)
  }
  if (!token && !actAsOrgId) return original(input, init)
  return original(input, { ...init, headers })
}

async function rotateRefreshToken(original: typeof fetch): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return false
  try {
    const res = await original(`${API_BASE_PATH}/auth/jwt/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false
    const data = (await res.json()) as {
      access_token?: string
      refresh_token?: string
    }
    if (!data.access_token || !data.refresh_token) return false
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
    return true
  } catch {
    return false
  }
}

export function installApiAuth(): void {
  if (authFetchInstalled || typeof window === 'undefined') return
  authFetchInstalled = true
  const original = window.fetch.bind(window)
  const base = new URL(API_BASE_PATH, window.location.href)
  const basePath = base.pathname.replace(/\/$/, '')
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let requestUrl: URL
    try {
      const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      requestUrl = new URL(raw, window.location.href)
    } catch {
      return original(input, init)
    }
    if (requestUrl.origin !== base.origin || !requestUrl.pathname.startsWith(`${basePath}/`)) {
      return original(input, init)
    }

    const path = `/api/v1${requestUrl.pathname.slice(basePath.length)}`
    let capture: { method: string; template: string; epoch: ReportCaptureEpoch; startedAt: number } | null = null
    try {
      const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
      const template = matchReportRequest(method, path)
      if (template) capture = { method, template, epoch: problemReportEpoch(), startedAt: performance.now() }
    } catch {
      // A collector setup failure cannot interfere with the request.
    }
    const record = (kind: 'http' | 'network' | 'timeout', res?: Response) => {
      if (!capture) return
      try {
        recordProblemReportFailure({
          method: capture.method,
          path_template: capture.template,
          status: res?.status,
          failure_kind: kind,
          duration_ms: Math.max(0, performance.now() - capture.startedAt),
          request_id: res?.headers.get('X-Request-Id'),
        }, capture.epoch)
      } catch {
        // Diagnostic collection must never change fetch results or report itself.
      }
    }
    try {
      let res: Response
      if (!USE_REAL_API) {
        res = await original(input, init)
      } else {
        res = await attachBearer(input, init, original, useAuthStore.getState().accessToken)
        // Token expiry is one logical request; only its final result is captured.
        if (res.status === 401 && !path.startsWith('/api/v1/auth/jwt/') && useAuthStore.getState().refreshToken) {
          if (!refreshInFlight) {
            refreshInFlight = rotateRefreshToken(original).finally(() => {
              refreshInFlight = null
            })
          }
          const refreshed = await refreshInFlight
          if (refreshed) {
            res = await attachBearer(input, init, original, useAuthStore.getState().accessToken, true)
          } else {
            // RootGuard reacts to cleared tokens and returns to login.
            useAuthStore.getState().clearTokens()
          }
        }
      }
      if (res.status >= 400 && res.status <= 599) record('http', res)
      return res
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        record(error instanceof Error && error.name === 'TimeoutError' ? 'timeout' : 'network')
      }
      throw error
    }
  }
}
